import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import TutorialWindow from "../components/TutorialWindow";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import { siliconeMoldTutorial } from "../utils/tutorialContents";
import { hull } from "../utils/hull";
import { normalizeModel } from "../utils/normalizeModel";
import ClipperLib from "clipper-lib";
import "../styles/common.css";
// NEW: 3D 打印机预设
const MACHINE_PRESETS = {
  printer3D: {
    "Prusa MK3S+": { width: 250, height: 250, depth: 210 },
    "Ender 3 V2": { width: 220, height: 220, depth: 250 },
    "Ultimaker S3": { width: 230, height: 190, depth: 200 },
    Custom: { width: 10, height: 10, depth: 10 },
  },
};

const exportCombinedSVGFromLines = (lines, filename = "combined.svg") => {
  let combinedPathData = "";

  lines.forEach((line, index) => {
    if (!line.geometry || !line.geometry.attributes.position) return;

    const posAttr = line.geometry.attributes.position;
    const points = [];
    for (let i = 0; i < posAttr.count; i++) {
      const x = posAttr.getX(i);
      const z = posAttr.getZ(i);
      points.push([x, -z]);
    }

    const pathData =
      "M " + points.map(([x, y]) => `${x},${y}`).join(" L ") + " Z";
    const color = index === 0 ? "red" : "green";
    combinedPathData += `<path d="${pathData}" fill="none" stroke="${color}" stroke-width="2"/>`;
  });

  if (combinedPathData) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-100 -100 200 200">${combinedPathData}</svg>`;
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
};

// 让模型最低点落到 y=0（可选是否顺便居中 XZ）
const dropMeshToGround = (mesh, { centerXZ = false } = {}) => {
  mesh.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(mesh);
  const centerX = (box.min.x + box.max.x) / 2;
  const centerZ = (box.min.z + box.max.z) / 2;

  const shift = new THREE.Vector3(
    centerXZ ? -centerX : 0,
    -box.min.y,
    centerXZ ? -centerZ : 0
  );

  mesh.position.add(shift);
  mesh.updateMatrixWorld(true);
};

const SiliconeMold = ({ stlFile }) => {
  const [constraintWarnings, setConstraintWarnings] = useState([]); // NEW

  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const objRef = useRef(null);
  const redLineRef = useRef(null);
  const greenLineRef = useRef(null);
  const loftRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
const fastIterNotRecommended = useMemo(() => true, []);
  const pageId = useMemo(() => "silicone-mold", []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  const { removeVisualHelpers, createTextLabel, createVisualization } =
    useVisualHelpers();

  const foamMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: 0x87ceeb,
        side: THREE.DoubleSide,
        roughness: 0.5,
        metalness: 0.3,
      }),
    []
  );

  const initScene = useCallback(() => {
    return initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );
  }, []);

  const createLoftSurface = useCallback((line, baseMesh) => {
    const height =
      new THREE.Box3().setFromObject(baseMesh).getSize(new THREE.Vector3()).y +
      10;
    const posAttr = line.geometry.attributes.position;
    const verts = [];
    for (let i = 0; i < posAttr.count; i++) {
      verts.push(new THREE.Vector3(posAttr.getX(i), 0, posAttr.getZ(i)));
    }

    const loftVerts = [];
    verts.forEach((v) => {
      loftVerts.push(v.clone());
      loftVerts.push(new THREE.Vector3(v.x, height, v.z));
    });

    const indices = [];
    for (let i = 0; i < loftVerts.length / 2 - 1; i++) {
      indices.push(i * 2, i * 2 + 1, i * 2 + 2);
      indices.push(i * 2 + 1, i * 2 + 3, i * 2 + 2);
    }

    console.warn("Creating loft surface:");
    console.warn("verts:", verts.length);
    console.warn("loftVerts:", loftVerts.length);
    console.warn("indices:", indices.length);

    if (loftVerts.length < 4 || indices.length % 3 !== 0) {
      console.warn("Invalid loft geometry — skipping setIndex.");
      return;
    }

    const loftGeom = new THREE.BufferGeometry().setFromPoints(loftVerts);
    loftGeom.setIndex(indices);
    loftGeom.computeVertexNormals();

    const material = new THREE.MeshStandardMaterial({
      color: 0x8a8a8a,
      roughness: 0.7,
      metalness: 0.1,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
    });

    const loft = new THREE.Mesh(loftGeom, material);
    loftRef.current = loft;
    sceneRef.current.add(loft);

    // Compute bounding box that includes both model and loft
    const combinedGroup = new THREE.Group();
    combinedGroup.add(baseMesh.clone(), loft.clone());
    const combinedBox = new THREE.Box3().setFromObject(combinedGroup);
    const size = combinedBox.getSize(new THREE.Vector3());

    // Save dimensions to display later
    setModelDimensions({
      width: size.x,
      height: size.y,
      depth: size.z,
    });

    createVisualization(combinedGroup, sceneRef.current, combinedBox);
  }, []);

  const createProjectionAndLoft = useCallback(
    (mesh) => {
      // Make sure the mesh is positioned with base at Y = 0

      const positions = mesh.geometry.attributes.position;
      if (!positions || positions.count < 3) {
        console.warn("Mesh geometry missing or invalid");
        return;
      }

      // Recalculate world positions after mesh is placed
      const xyPoints = [];
      for (let i = 0; i < positions.count; i++) {
        const vertex = new THREE.Vector3(
          positions.getX(i),
          positions.getY(i),
          positions.getZ(i)
        ).applyMatrix4(mesh.matrixWorld);
        xyPoints.push(new THREE.Vector2(vertex.x, vertex.z));
      }

      const hull2d = hull(
        xyPoints.map((p) => [p.x, p.y]),
        10
      );
      if (!hull2d || hull2d.length < 3) return;

      // RED LINE (original contour)
      const redLinePts = hull2d.map(([x, z]) => new THREE.Vector3(x, 0, z));
      redLinePts.push(redLinePts[0].clone());

      const redGeom = new THREE.BufferGeometry().setFromPoints(redLinePts);
      const redLine = new THREE.Line(
        redGeom,
        new THREE.LineBasicMaterial({ color: 0xff0000 })
      );
      redLineRef.current = redLine;
      sceneRef.current.add(redLine);

      // GREEN LINE (offset boundary)
      const offsetScale = 5;
      const scaled = hull2d.map(([x, y]) => ({
        X: x * offsetScale,
        Y: y * offsetScale,
      }));
      const co = new ClipperLib.ClipperOffset();
      co.AddPath(
        scaled,
        ClipperLib.JoinType.jtRound,
        ClipperLib.EndType.etClosedPolygon
      );
      const solution = new ClipperLib.Paths();
      co.Execute(solution, 10 * offsetScale);

      if (!solution.length || !solution[0].length) return;

      const offsetPts = solution[0].map(
        (pt) => new THREE.Vector3(pt.X / offsetScale, 0, pt.Y / offsetScale)
      );
      offsetPts.push(offsetPts[0].clone());

      const greenGeom = new THREE.BufferGeometry().setFromPoints(offsetPts);
      const greenLine = new THREE.Line(
        greenGeom,
        new THREE.LineBasicMaterial({ color: 0x00ff00 })
      );
      greenLineRef.current = greenLine;
      sceneRef.current.add(greenLine);

      // LOFT from green line to top
      createLoftSurface(greenLine, mesh);
    },
    [createLoftSurface]
  );

  const exportSVG = useCallback(() => {
    if (!redLineRef.current || !greenLineRef.current) return;
    exportCombinedSVGFromLines(
      [redLineRef.current, greenLineRef.current],
      "silicone_mold_combined"
    );
    setTutorialVisible(true);
  }, []);

  const loadModel = useCallback(
    (filePath) => {
      setIsLoading(true);
      setError(null);

      if (objRef.current) {
        sceneRef.current?.remove(objRef.current);
        objRef.current.geometry?.dispose();
        if (Array.isArray(objRef.current.material)) {
          objRef.current.material.forEach((m) => m.dispose());
        } else {
          objRef.current.material?.dispose();
        }
        objRef.current = null;
      }

      removeVisualHelpers(sceneRef.current);

      const loader = new STLLoader();
loader.load(
  filePath || "/models/twistT.stl",
  (geometry) => {
    try {
      if (!geometry.attributes?.position || geometry.attributes.position.count < 3) {
        throw new Error("Invalid or empty geometry");
      }

      const mesh = new THREE.Mesh(geometry, foamMaterial);
      normalizeModel(mesh);
      dropMeshToGround(mesh, { centerXZ: false }); // 放到底部

      // ★★★ 先算模型包围盒尺寸（仍未 add 到 scene）
      mesh.updateMatrixWorld(true);
      const bbox = new THREE.Box3().setFromObject(mesh);
      const size = new THREE.Vector3();
      bbox.getSize(size); // width=x, height=y, depth=z

      // 取打印机限制（优先 window.labConstraints）
      const selectedPrinter =
        window?.labConstraints?.machineSettings?.printer3D?.selected || "Prusa MK3S+";
      const limits =
        MACHINE_PRESETS.printer3D[selectedPrinter] || MACHINE_PRESETS.printer3D["Prusa MK3S+"];

      // 检查是否超出
      const exceeds =
        size.x > limits.width || size.y > limits.height || size.z > limits.depth;

      if (exceeds) {
        setConstraintWarnings([{
          type: "size_exceeded",
          part: "Silicone Mold Model",
          currentSize: {
            width: size.x.toFixed(1),
            height: size.y.toFixed(1),
            depth: size.z.toFixed(1),
          },
          maxSize: limits,
          printer: selectedPrinter,
        }]);
        // ★★★ 不往下做任何 heavy 操作，不 add 到 scene
        setIsLoading(false);
        return;
      } else {
        setConstraintWarnings([]); // 清空历史警告
      }

      // ★★★ 只有通过检查才 add 到 scene、才做 loft
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      objRef.current = mesh;
      sceneRef.current.add(mesh);

      setModelDimensions({ width: size.x, height: size.y, depth: size.z });

      createProjectionAndLoft(mesh); // 继续你的 loft 可视化
      setError(null);
      setIsLoading(false);
    } catch (err) {
      console.error("Error processing STL geometry:", err);
      setError("Failed to process 3D geometry");
      setIsLoading(false);
    }
  },
  undefined,
  (err) => {
    console.error("Failed to load STL:", err);
    setError("Failed to load STL file");
    setIsLoading(false);
  }
);

    },
    [foamMaterial, removeVisualHelpers, createProjectionAndLoft]
  );

  useEffect(() => {
    const cleanup = initScene();
    const scene = sceneRef.current;

    if (!stlFile) {
      window.addEventListener("message", (event) => {
        if (typeof event.data === "string" && event.data.startsWith("blob:")) {
          loadModel(event.data);
        }
      });
      window.parent.postMessage("requestModelPath", "*");
      setTimeout(() => loadModel("/models/twistT.stl"), 1000);
    } else {
      loadModel(stlFile);
    }

    return () => {
      if (scene) removeVisualHelpers(scene);
      cleanup();
    };
  }, [initScene, loadModel, stlFile, removeVisualHelpers]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container" />
      {constraintWarnings.length > 0 && (
  <div style={{
    position: "fixed",
    top: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
  }}>
    {constraintWarnings.map((warning, index) => (
      <div key={index} style={{
        backgroundColor: "#ff9800",
        color: "white",
        padding: "12px 16px",
        borderRadius: "6px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
        maxWidth: "90vw",
        width: "fit-content",
        position: "relative",
      }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
          <span style={{ fontSize: "16px", marginRight: "8px" }}>⚠️</span>
          <strong>{warning.part} Exceeds {warning.printer} Size</strong>
        </div>
        <div style={{ fontSize: "13px" }}>
          <div>Current: {warning.currentSize.width} × {warning.currentSize.height} × {warning.currentSize.depth} mm</div>
          <div>Printer Max: {warning.maxSize.width} × {warning.maxSize.height} × {warning.maxSize.depth} mm</div>
        </div>
        <button
          onClick={() => setConstraintWarnings(prev => prev.filter((_, i) => i !== index))}
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            background: "none",
            border: "none",
            color: "white",
            fontSize: "16px",
            cursor: "pointer",
          }}
        >
          ×
        </button>
      </div>
    ))}
  </div>
)}

      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="panel-toggle-button"
      >
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      {!isLoading && expanded && (
        <div className={`controls-panel ${panelCollapsed ? "collapsed" : ""}`}>
         <div className="panel-title-row">
  <div
    className="panel-title"
    onClick={() => setPanelCollapsed(!panelCollapsed)}
  >
    Silicone Mold
    <span className="collapse-icon">{panelCollapsed ? "▼" : "▲"}</span>
  </div>

  {fastIterNotRecommended && (
    <span
      className="iteration-badge badge--warn"
      title="3D printing a master + silicone prep + casting + curing = lots of manual time"
    >
      Not ideal for quick iteration
    </span>
  )}
</div>


          {!panelCollapsed && (
            <>
              <div className="panel-section">
                <div className="section-header">
                  <h4>Workflow Information</h4>
                </div>
                <div className="section-content">
  <p>
          This workflow helps you prepare a 3D printed master model for
          silicone mold making. It uses laser-cut outlines based on projected
          contours of the model to form the mold base and walls, with a vertical
          loft to define mold depth.
        </p>
        <p>
          Typically, the mold is cast in two pours to capture both the base and
          side walls, enabling the production of detailed food-safe shapes such
          as chocolate, mousse cakes, and other edible creations.
        </p>

                </div>
              </div>

              <div className="panel-section">
  <div className="section-header"><h4>Time & Effort</h4></div>
  <div className="section-content">
    <ul style={{ marginLeft: 16, lineHeight: 1.5, fontSize: 13 }}>
      <li><strong>3D printing master</strong>: print, support removal, surface prep.</li>
      <li><strong>Mold prep</strong>: mold box, release agent, draft checks, registration keys, sealing edges.</li>
      <li><strong>Mix & casting</strong>: accurate ratios, degassing if possible, controlled pour.</li>
      <li><strong>Cure & demold</strong>: curing time, trimming, cleanup; often iterative.</li>
    </ul>
    <p style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
      Tip: validate geometry with a small section or scaled test pour before committing to full size.
    </p>
  </div>
</div>


              <div className="panel-section">
                <div className="section-header">
                  <h4>Export & Instructions</h4>
                </div>
                <div className="section-content">
                  <p>
                    Export a combined SVG file with both the model contour and
                    mold wall outline. This can be used directly for laser
                    cutting your mold base.
                  </p>
                </div>
              </div>

              <div className="buttons-container">
                <button onClick={exportSVG} className="action-button">
                  EXPORT SVG TEMPLATES
                </button>
                <button
                  onClick={() => setTutorialVisible(!tutorialVisible)}
                  className="action-button"
                >
                  {tutorialVisible
                    ? "HIDE INTERACTIVE GUIDANCE"
                    : "SHOW INTERACTIVE GUIDANCE"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {isLoading && <div className="loading-box">Loading model...</div>}
      {error && <div className="error-box">Error: {error}</div>}

      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={() => setTutorialVisible(!tutorialVisible)}
        tutorialContent={siliconeMoldTutorial}
      />
    </div>
  );
};

export default SiliconeMold;
